import { useMemo, useState } from "react";
import ScrollContainer from "react-indiana-drag-scroll";

import Calendar from "~/components/calendar";
import MainButton from "~/components/common/mainButton";
import FullFilter from "~/components/ui/fullFilter";
import Modal from "~/components/ui/modal";
import { useLanguage } from "~/contexts/providers/language";
import englishTranslations from "~/localization/en";
import frenchTranslations from "~/localization/fr";
import { EventType } from "~/types/event/event.types";
import { FullFilterType } from "~/types/filter/filter.types";
import { LocationEnum } from "~/types/location/locationEnums.types";
import { TagType } from "~/types/tag/tag.types";
import { formatSimpleDate } from "~/utils/functions/date.functions";
import { getFilteredEvents } from "~/utils/functions/events.functions";
import { formatNumber } from "~/utils/functions/string.functions";

import HighlightsCarousel from "../highlightsCarousel";
import WeekScroller from "../weekScroller";
import styles from "./index.module.scss";
import { orderByWeek } from "./utils";

interface Props {
  events: EventType[];
  tags: TagType[];
}

const Programme = ({ events, tags }: Props) => {
  const [fullFilter, setFullFilter] = useState<FullFilterType>({
    locations: [],
    capacities: [],
    tags: [],
  });
  const [open, setOpen] = useState(false);

  const { language } = useLanguage();
  const accountTexts =
    language === "En"
      ? englishTranslations.accountPage
      : frenchTranslations.accountPage;

  const filteredEvents = useMemo(() => {
    return orderByWeek(getFilteredEvents(events, fullFilter));
  }, [events, fullFilter]);

  const highlightedEvents = useMemo(() => {
    return events.filter((el) => el.highlighted);
  }, [events]);

  const showHighlights = useMemo(
    () =>
      !fullFilter.locations.length &&
      !fullFilter.tags.length &&
      !fullFilter.dates?.dateFrom &&
      !fullFilter.dates?.dateTo &&
      highlightedEvents.length,
    [fullFilter, highlightedEvents]
  );

  const handleResetFilter = () => {
    setFullFilter({ locations: [], capacities: [], tags: [] });
  };

  const handleSetLocationFilter = (filterName: LocationEnum) => {
    setFullFilter((prev) => {
      const locations = prev.locations.includes(filterName)
        ? prev.locations.filter((locations) => locations !== filterName)
        : [...prev.locations, filterName];
      return { ...prev, locations };
    });
  };

  const handleSetTagFilter = (filterName: TagType) => {
    setFullFilter((prev) => {
      const tags = prev.tags.includes(filterName)
        ? prev.tags.filter((tag) => tag !== filterName)
        : [...prev.tags, filterName];
      return { ...prev, tags };
    });
  };

  const handleSetDateFilter = (dateFrom: Date, dateTo: Date) => {
    setFullFilter((prev) => ({ ...prev, dates: { dateFrom, dateTo } }));
  };

  return (
    <>
      <ScrollContainer className={styles.filterScroll}>
        <MainButton
          className={[
            styles.item,
            !!fullFilter.dates ? styles.selected : null,
          ].join(" ")}
          onClick={() => {
            setOpen(true);
          }}
        >
          {accountTexts.calendar}{" "}
          {fullFilter.dates?.dateFrom &&
            fullFilter.dates?.dateTo &&
            `${formatSimpleDate(
              fullFilter.dates.dateFrom,
              language
            )} → ${formatSimpleDate(fullFilter.dates.dateTo, language)}`}
        </MainButton>
        <FullFilter
          tags={tags}
          useCapacityFilter={false}
          fullFilter={fullFilter}
          handleResetFilter={handleResetFilter}
          handleSetLocationFilter={handleSetLocationFilter}
          handleSetCapacityFilter={() => {}}
          handleSetTagFilter={handleSetTagFilter}
        />
      </ScrollContainer>

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        showAnimation="expand"
      >
        <div className={styles.calendarModalContainer}>
          <Calendar
            handleCloseCalendar={() => {
              setOpen(false);
            }}
            handleSetDateFilter={handleSetDateFilter}
            value={fullFilter.dates}
          />
          <MainButton
            className={styles.removeDateFilter}
            disabled={!fullFilter.dates}
            onClick={() => {
              setFullFilter((prev) => ({ ...prev, dates: undefined }));
              setOpen(false);
            }}
          >
            {language === "En" ? "Reset" : "Réinitialiser"}
          </MainButton>
        </div>
      </Modal>

      {showHighlights && (
        <span className={styles.highlightsContainer}>
          <div className={styles.titleContainer}>
            <span>Highlights</span>
            <h5>{`${formatNumber(highlightedEvents.length)} ${
              accountTexts.event
            }${highlightedEvents.length > 1 ? "s" : ""}`}</h5>
          </div>
          <HighlightsCarousel events={highlightedEvents} />
        </span>
      )}

      <div className={styles.eventsGrid}>
        {Object.entries(filteredEvents)
          .sort(([_keyA, a], [_keyB, b]) => (a.date < b.date ? -1 : 1))
          .map(([_key, { header, data }], index) => {
            return <WeekScroller weekHeader={header} data={data} key={index} />;
          })}
      </div>
    </>
  );
};

export default Programme;
